import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Alert, Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import {
  RestrictedByCapability,
  RestrictedByCapabilityWithDebug,
} from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageGrid, splitStrategy } from "../../commons/layout/PageGrid.js";
// import { OrganisationAgreementTemplatesBox } from "../../features/organisations/boxes/OrganisationAgreementTemplatesBox.js";
import { OrganisationCustomFieldConfigBox } from "../../features/organisations/boxes/OrganisationCustomFieldConfigBox.js";
import { OrganisationAutomatedOnboardingBox } from "../../features/organisations/boxes/OrganisationAutomatedOnboardingBox.js";
import { OrganisationAutomatedWorkflowsBox } from "../../features/organisations/boxes/OrganisationAutomatedWorkflowsBox.js";
import { OrganisationEmailSettingsBox } from "../../features/organisations/boxes/OrganisationEmailSettingsBox.js";
import { OrganisationEmbeddableWizardSnippetBox } from "../../features/organisations/boxes/OrganisationEmbeddableWizardSnippetBox.js";
import { OrganisationProjectDefaultSettingsBox } from "../../features/organisations/boxes/OrganisationProjectDefaultSettingsBox.js";
import { OrganisationProjectImportSettingsBox } from "../../features/organisations/boxes/OrganisationProjectImportSettingsBox.js";
import { OrganisationReferralBox } from "../../features/organisations/boxes/OrganisationReferralBox.js";
import { INTEGRATIONS_BASE_PATH } from "../integrations/paths.js";
import {
  useOrganisationSettingsIntegrationsQuery,
  useOrganisationSettingsIntegrations_InvalidatePrefixOrgCacheMutation,
} from "./OrganisationSettingsIntegrations.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationSettingsIntegrations = ({ submenuItems }: Props) => {
  const { t } = useTranslate(["OrganisationPageTopbar", "Integrations"]);
  const navigate = useNavigate();

  const client = useApolloClient();
  const query = useOrganisationSettingsIntegrationsQuery({
    client,
    fetchPolicy: "cache-and-network",
  });

  const [
    invalidatePrefixOrgCache,
    { loading: invalidatePrefixOrgCacheLoading },
  ] = useOrganisationSettingsIntegrations_InvalidatePrefixOrgCacheMutation({
    client,
  });

  const user = getDataOrNull(query.data?.viewer);
  const organisation = user?.organisation;
  const organisationSettings = getDataOrNull(query.data?.organisationSettings);
  const organisationEmailSettings = getDataOrNull(
    query.data?.organisationEmailSettings
  );
  const organisationEmailTemplates =
    getDataOrNull(query.data?.organisationEmailTemplates)?.emailTemplates ?? [];
  const organisationWorkflows =
    getDataOrNull(query.data?.organisationWorkflows)?.workflows ?? [];
  const organisationAutomatedOnboardingSettings = getDataOrNull(
    query.data?.organisationAutomatedOnboardingSettings
  );
  return (
    <Page title={t("Data")} submenuItems={submenuItems}>
      {organisation && user && organisationSettings && (
        <PageContainer>
          {/* Transitional pointer to the relocated integrations module —
              removal is tracked on the post-merge checklist */}
          <RestrictedByCapability capability="integrations">
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(INTEGRATIONS_BASE_PATH)}
                >
                  {t("Go to integrations", { ns: "Integrations" })}
                </Button>
              }
            >
              {t(
                "Integrations have moved! You can now find them under Settings → All integrations.",
                { ns: "Integrations" }
              )}
            </Alert>
          </RestrictedByCapability>
          {/* Boxes are direct children so splitStrategy distributes them
              evenly across the columns */}
          <PageGrid columns={{ xs: 1, md: 2, xl: 3 }} strategy={splitStrategy}>
            <OrganisationProjectDefaultSettingsBox
              organisationId={organisation.id}
              organisationSettings={organisationSettings}
            />
            <OrganisationProjectImportSettingsBox />
            <RestrictedByCapabilityWithDebug capability="referral">
              <OrganisationReferralBox />
            </RestrictedByCapabilityWithDebug>
            {/* One grid slot for both boxes, so splitStrategy never puts a
                column break between them and the onboarding box always sits
                directly below the workflows box */}
            <>
              <RestrictedByOrganisationPermissionWithDebug permission="organisation_integration_update">
                <OrganisationAutomatedWorkflowsBox
                  workflows={organisationWorkflows}
                  emailTemplates={organisationEmailTemplates}
                  organisationId={organisation.id}
                />
              </RestrictedByOrganisationPermissionWithDebug>
              <RestrictedByOrganisationPermissionWithDebug permission="organisation_settings_update">
                {organisationAutomatedOnboardingSettings ? (
                  <OrganisationAutomatedOnboardingBox
                    settings={organisationAutomatedOnboardingSettings}
                  />
                ) : null}
              </RestrictedByOrganisationPermissionWithDebug>
            </>
            <OrganisationCustomFieldConfigBox />
            {organisationEmailSettings ? (
              <OrganisationEmailSettingsBox
                organisation={organisation}
                organisationEmailSettings={organisationEmailSettings}
                organisationEmailTemplates={organisationEmailTemplates}
              />
            ) : null}
            <RestrictedByCapabilityWithDebug capability="templating">
              <OrganisationEmbeddableWizardSnippetBox
                organisation={organisation}
                organisationSettings={organisationSettings}
              />
            </RestrictedByCapabilityWithDebug>
            {/* <OrganisationAgreementTemplatesBox /> */}
            <button
              type="button"
              disabled={invalidatePrefixOrgCacheLoading}
              onClick={() => {
                invalidatePrefixOrgCache();
              }}
            >
              invalidate organisation cache{" "}
              {invalidatePrefixOrgCacheLoading ? "..." : ""}
            </button>
          </PageGrid>
        </PageContainer>
      )}
    </Page>
  );
};
